import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../api';

const AuthContext = createContext(null);

// ── Decode JWT expiry without a library ─────────────────────────────────────
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [admin,       setAdmin]       = useState(() => {
    const stored = localStorage.getItem('admin');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading,     setLoading]     = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // ── Central logout ───────────────────────────────────────────────────────
  const logout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    setAdmin(null);
    if (reason === 'expired') {
      // Small delay so the toast system is mounted before redirect
      setTimeout(() => {
        window.location.href = '/login?reason=expired';
      }, 100);
    }
  }, []);

  // ── Listen for 401 events fired by the axios interceptor ────────────────
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [logout]);

  // ── On mount: validate token + schedule auto-logout at expiry ────────────
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      setAuthChecked(true);
      return;
    }

    // Check if already expired before even calling the server
    const expiry = getTokenExpiry(token);
    if (expiry && Date.now() >= expiry) {
      logout('expired');
      setAuthChecked(true);
      return;
    }

    // Fetch fresh admin data (picks up role changes etc.)
    API.get('/auth/me')
      .then(({ data }) => {
        if (data?.admin) {
          setAdmin(data.admin);
          localStorage.setItem('admin', JSON.stringify(data.admin));

          // Schedule auto-logout when token actually expires
          if (expiry) {
            const msUntilExpiry = expiry - Date.now();
            const timer = setTimeout(() => logout('expired'), msUntilExpiry);
            return () => clearTimeout(timer);
          }
        }
      })
      .catch(() => {
        logout();
      })
      .finally(() => setAuthChecked(true));
  }, [logout]);

  // ── Login ────────────────────────────────────────────────────────────────
  const login = async (username, password) => {
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', { username, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      setAdmin(data.admin);

      // Schedule auto-logout for new token
      const expiry = getTokenExpiry(data.token);
      if (expiry) {
        const msUntilExpiry = expiry - Date.now();
        setTimeout(() => logout('expired'), msUntilExpiry);
      }

      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  // ── Block render until auth check completes ──────────────────────────────
  if (!authChecked) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--bg)',
      }}>
        <span className="spinner" style={{ borderTopColor: 'var(--primary)', width: 36, height: 36 }} />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ admin, login, logout, loading, isLoggedIn: !!admin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);